// ============================================
// SDSubgraph.jsx — "S/D" Supply/Demand pressure subgraph.
//
// Two smoothed lines:
//   Blue = up (buy) pressure
//   Red  = down (sell) pressure
// Higher line sits above the other; crosses mark the flip.
//
// Per-bar pressure folds volume × travel-efficiency × speed so heavy volume
// that goes nowhere (absorption) prints weak, while thin/fast travel prints
// strong — matching the absorption → weak-down → low-vol rip story.
//
// CONTRACT: same scroll-sync discipline as DeltaVolumePro / CVD — X from
// frameRef via subgraphX inside FRAME_LAYER rAF, never React state.
// ============================================
import { useEffect, useRef, useState } from 'react'
import { FIXED_OVERLAY_WIDTH, ROW_HEIGHT } from '../utils/chartUtils'
import { placeCrosshairs, registerCrosshairEl, CROSSHAIR_PANE_STYLE } from '../utils/crosshair'
import { setCtxFont } from '../utils/canvasFont'
import { requestFrame, cancelFrame, FRAME_LAYER } from '../utils/frameDriver'
import { subgraphX } from '../utils/subgraphX'

export const SD_DEFAULTS = {
  smoothLen: 10,          // EMA length (1 = raw pulse)
  speedWeight: 1.0,       // 0 = vol×eff only; 1 = default speed blend
  mode: 'pulse',          // 'pulse' = EMA of per-bar; 'run' = EMA of running sum
  upCol: '#2f6fd6',
  downCol: '#ef5350',
}

// Buy/sell from the same footprint fields DVP/CVD use. Pure-kline bars
// (no flow) fall back to total volume_tokens attributed by bar direction.
function barFlow(c) {
  if (!c) return { buy: 0, sell: 0 }
  if (c._liveLevels && c._liveLevels.size) {
    let buy = 0, sell = 0
    for (const lvl of c._liveLevels.values()) { buy += lvl.buy || 0; sell += lvl.sell || 0 }
    return { buy, sell }
  }
  if (c._native) {
    const tot = c._nativeTotalVol || 0
    const d = c._nativeDelta || 0
    return { buy: Math.max(0, (tot + d) / 2), sell: Math.max(0, (tot - d) / 2) }
  }
  const tot = c.volume_tokens || 0
  if (!(tot > 0)) return { buy: 0, sell: 0 }
  const o = c.open_mcap, cl = c.close_mcap
  if (!(o > 0) || !(cl > 0) || cl === o) return { buy: tot / 2, sell: tot / 2 }
  return cl > o ? { buy: tot, sell: 0 } : { buy: 0, sell: tot }
}

// Cache key: length + cfg + last-bar flow + sparse _dataVersion fingerprint.
// Invalidates on live trades / hist upgrades without scanning every level twice.
function makeSdKey(cs, cfg, bm) {
  const n = cs?.length | 0
  if (!n) return '0'
  const last = cs[n - 1]
  const f = barFlow(last)
  let vs = 0
  const step = Math.max(1, (n / 16) | 0)
  for (let i = 0; i < n; i += step) vs = (vs + (cs[i]._dataVersion | 0)) | 0
  vs = (vs + (last._dataVersion | 0)) | 0
  return [
    n,
    cfg.smoothLen | 0,
    Math.round((Number(cfg.speedWeight) || 0) * 1000),
    cfg.mode || 'pulse',
    bm,
    last.startTime || 0,
    f.buy || 0,
    f.sell || 0,
    last.close_mcap || 0,
    vs,
  ].join('|')
}

export function computeSd(candles, cfg, barMinutes = 1, outBuf = null) {
  const n = candles?.length || 0
  const reuse = outBuf && outBuf.up && outBuf.up.length === n
  const out = reuse ? outBuf : {
    up: new Float64Array(n), dn: new Float64Array(n),
    buy: new Float64Array(n), sell: new Float64Array(n),
  }
  if (!out.buy || out.buy.length !== n) {
    out.buy = new Float64Array(n)
    out.sell = new Float64Array(n)
  }
  if (!n) return out
  const eps = 1e-12
  const sw = Math.max(0, Number(cfg.speedWeight) || 0)
  const bm = Math.max(Number(barMinutes) || 1, 1 / 60)
  // Soft reference: ~0.5% mcap travel per minute reads as "fast" on Solana 1m/5m.
  const refSpeed = 0.005
  const rawU = new Float64Array(n)
  const rawD = new Float64Array(n)
  const buyA = out.buy, sellA = out.sell
  for (let i = 0; i < n; i++) {
    buyA[i] = 0; sellA[i] = 0
    const c = candles[i]
    if (!c) continue
    const o = c.open_mcap, cl = c.close_mcap
    if (!(o > 0) || !(cl > 0)) continue
    const hi = Math.max(c.high_mcap || o, o, cl)
    const lo = Math.min(c.low_mcap || o, o, cl)
    const move = Math.abs(cl - o)
    const range = Math.max(hi - lo, eps)
    const eff = move / range                          // 0..1 travel vs full range
    const speed = (move / o) / bm                     // fraction of mcap per minute
    // pressure = vol × eff × (1 + sw × speed/ref)
    // Heavy vol + tiny travel → low eff and low speed → soft pressure (absorption).
    // Thin vol + fast travel → high speed boost → strong pressure (the rip).
    const mult = eff * (1 + sw * (speed / refSpeed))
    const { buy, sell } = barFlow(c)
    buyA[i] = buy; sellA[i] = sell
    rawU[i] = buy * mult
    rawD[i] = sell * mult
  }
  // Optional running sum before smooth — keeps one line above longer.
  let srcU = rawU, srcD = rawD
  if (cfg.mode === 'run') {
    srcU = new Float64Array(n)
    srcD = new Float64Array(n)
    let su = 0, sd = 0
    for (let i = 0; i < n; i++) { su += rawU[i]; sd += rawD[i]; srcU[i] = su; srcD[i] = sd }
  }
  const len = Math.max(1, cfg.smoothLen | 0)
  if (len <= 1) {
    out.up.set(srcU); out.dn.set(srcD)
    return out
  }
  const k = 2 / (len + 1)
  let eu = srcU[0], ed = srcD[0]
  out.up[0] = eu; out.dn[0] = ed
  for (let i = 1; i < n; i++) {
    eu = srcU[i] * k + eu * (1 - k)
    ed = srcD[i] * k + ed * (1 - k)
    out.up[i] = eu
    out.dn[i] = ed
  }
  return out
}

const fmtV = (v) => {
  if (!v) return '0'
  const a = Math.abs(v)
  const t = a >= 1e9 ? Math.round(a / 1e9) + 'B'
          : a >= 1e6 ? Math.round(a / 1e6) + 'M'
          : a >= 1e3 ? Math.round(a / 1e3) + 'K'
          : String(Math.round(a))
  return t
}

const SD_SCALE_KEY = 'solcheddar:sdXScale'
const SD_SCALES = [1, 2, 3, 5, 8]

export default function SDSubgraph({
  candles,
  viewEndIndexRef,
  viewWindowSizeRef,
  containerWidthRef,
  paintNowRef,
  axisWidth,
  fractionalOffsetPxRef,
  frameRef,
  mouseXRef,
  rowHeightRef,
  onCursorXChange,
  config = SD_DEFAULTS,
  height = 110,
  barMinutes = 1,
}) {
  const plotRef = useRef(null)
  const rafRef = useRef(0)
  const hlRef = useRef(null)
  const chipRef = useRef(null)
  const yScaleRef = useRef({ yMin: 0, yMax: 1, topPad: 6, botPad: 6, H: 0 })
  const xGeomRef = useRef({ cw: 1, frac: 0, frontAdj: 0, endIdx: 0, mainCw: 1 })
  const selfCursorXRef = useRef(-1)
  const xhairRef = useRef(null)
  useEffect(() => registerCrosshairEl(mouseXRef, xhairRef.current), [mouseXRef])

  const [xScale, setXScale] = useState(() => {
    try {
      const v = Number(localStorage.getItem(SD_SCALE_KEY))
      return SD_SCALES.includes(v) ? v : 1
    } catch { return 1 }
  })
  const [scaleUiHot, setScaleUiHot] = useState(false)
  const xScaleRef = useRef(xScale)
  useEffect(() => {
    xScaleRef.current = xScale
    try { localStorage.setItem(SD_SCALE_KEY, String(xScale)) } catch {}
  }, [xScale])
  const seriesRef = useRef({ up: new Float64Array(0), dn: new Float64Array(0), buy: new Float64Array(0), sell: new Float64Array(0) })
  const sdCacheRef = useRef({ key: '', series: null })
  const cfgRef = useRef(config)
  cfgRef.current = { ...SD_DEFAULTS, ...config }
  const barMinRef = useRef(barMinutes)
  barMinRef.current = barMinutes

  const wrapRef = useRef(null)

  useEffect(() => {
    const cv = plotRef.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    let live = true
    const draw = () => {
      if (!live) return
      rafRef.current = requestFrame(draw, FRAME_LAYER)
      const W = containerWidthRef.current || cv.parentElement?.clientWidth || 300
      const H = height
      const dpr = window.devicePixelRatio || 1
      if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) {
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr)
        cv.style.width = W + 'px'; cv.style.height = H + 'px'
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, W, H)
      const f = frameRef ? frameRef.current : null
      const cs = (f && f.candles) || candles || []
      const cfgNow = cfgRef.current
      // Recompute only when data/settings fingerprint moves. Scroll/pan
      // reuses the cached series — the expensive part was barFlow×n + EMA
      // every rAF, then a second barFlow pass for thicky.
      const cache = sdCacheRef.current
      const key = makeSdKey(cs, cfgNow, barMinRef.current)
      if (!cache.series || cache.key !== key) {
        cache.series = computeSd(cs, cfgNow, barMinRef.current, cache.series)
        cache.key = key
      }
      seriesRef.current = cache.series
      const S = cache.series
      const n = Math.min(cs.length, S.up.length)
      if (!n) return
      const plotW = Math.max(50, W - (axisWidth || FIXED_OVERLAY_WIDTH))
      const g = subgraphX(f, xScaleRef.current || 1)
      if (!g) return
      const startIdx = g.start
      const dataEnd = Math.min(g.end, n - 1)
      const cw = g.cw
      const X = g.xLeft
      // Y scale to visible data min/max (+ pad). Pressure stays non-negative
      // from computeSd; BOTH up and dn plot on the same side. Draw 0 midline
      // only when 0 falls inside the padded range (no forced symmetric +/- mx).
      let yMin = Infinity, yMax = -Infinity
      for (let i = startIdx; i <= dataEnd; i++) {
        const a = S.up[i], b = S.dn[i]
        if (Number.isFinite(a)) { if (a < yMin) yMin = a; if (a > yMax) yMax = a }
        if (Number.isFinite(b)) { if (b < yMin) yMin = b; if (b > yMax) yMax = b }
      }
      if (!(yMax > yMin)) { yMin = 0; yMax = 1 }
      const pad = (yMax - yMin) * 0.12 || Math.abs(yMax) * 0.12 || 1
      yMin -= pad
      yMax += pad
      const topPad = 6, botPad = 6
      const span = H - topPad - botPad
      const yRange = yMax - yMin
      const yOf = (v) => topPad + ((yMax - v) / yRange) * span
      yScaleRef.current = { yMin, yMax, topPad, botPad, H }
      xGeomRef.current = g

      // Zero midline when 0 is in range
      if (yMin <= 0 && yMax >= 0) {
        const midY = yOf(0)
        ctx.strokeStyle = 'rgba(180,190,210,0.55)'
        ctx.lineWidth = 1
        ctx.setLineDash([3, 3])
        ctx.beginPath(); ctx.moveTo(0, midY); ctx.lineTo(plotW, midY); ctx.stroke()
        ctx.setLineDash([])
      }

      // Visual only: buy→blue thick, sell→red thick (cached flow). Both
      // lines plot ABOVE the 0 midline (positive pressure). QUANTIZE width
      // into buckets and batch adjacent same-bucket segments into one stroke.
      const WMIN = 1, WMAX = 3.5, NBUCKETS = 5
      const buyArr = S.buy, sellArr = S.sell
      let vmax = 1e-9
      for (let i = startIdx; i <= dataEnd; i++) {
        const b = buyArr[i] || 0, sl = sellArr[i] || 0
        if (b > vmax) vmax = b
        if (sl > vmax) vmax = sl
      }
      const widthSide = (sideArr, i) =>
        WMIN + (WMAX - WMIN) * Math.min(1, Math.max(0, (sideArr[i] || 0) / vmax))
      const quantW = (w) => {
        const t = Math.min(1, Math.max(0, (w - WMIN) / (WMAX - WMIN)))
        const b = Math.round(t * (NBUCKETS - 1))
        return WMIN + (WMAX - WMIN) * (b / (NBUCKETS - 1))
      }
      const strokeLine = (arr, col, sideArr) => {
        ctx.strokeStyle = col
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        let prevX = 0, prevY = 0, prevI = -1, hasPrev = false
        let runW = -1, open = false
        const flush = () => { if (open) { ctx.stroke(); open = false } }
        for (let i = startIdx; i <= dataEnd; i++) {
          const v = arr[i]
          if (!Number.isFinite(v)) { flush(); hasPrev = false; runW = -1; continue }
          const x = X(i) + cw / 2
          const y = yOf(v) // both lines same side (pressure ≥ 0)
          // Skip segments wholly off the plot (xScale > 1 can leave tails).
          if (hasPrev) {
            if ((x < -4 && prevX < -4) || (x > plotW + 4 && prevX > plotW + 4)) {
              prevX = x; prevY = y; prevI = i
              continue
            }
            const w = quantW((widthSide(sideArr, prevI) + widthSide(sideArr, i)) / 2)
            if (!open || w !== runW) {
              flush()
              runW = w
              ctx.lineWidth = w
              ctx.beginPath()
              ctx.moveTo(prevX, prevY)
              open = true
            }
            ctx.lineTo(x, y)
          }
          prevX = x; prevY = y; prevI = i; hasPrev = true
        }
        flush()
      }
      strokeLine(S.dn, cfgNow.downCol || SD_DEFAULTS.downCol, sellArr)
      strokeLine(S.up, cfgNow.upCol || SD_DEFAULTS.upCol, buyArr)

      // Axis gutter ticks
      setCtxFont(ctx, '400 14px "Nova Square", system-ui, sans-serif')
      ctx.textAlign = 'center'
      ctx.fillStyle = '#646464'
      const axisMidX = plotW + (W - plotW) / 2
      const N_TICKS = 5
      for (let t = 0; t < N_TICKS; t++) {
        const frac = t / (N_TICKS - 1)
        const v = yMax - frac * yRange
        const y = Math.max(11, Math.min(H - 4, topPad + frac * span))
        const label = Math.abs(v) < yRange * 0.02 ? '0' : (v < 0 ? '-' : '') + fmtV(v)
        ctx.fillText(label, axisMidX, y)
      }
      const lastU = S.up[dataEnd], lastD = S.dn[dataEnd]
      if (Number.isFinite(lastU) && Number.isFinite(lastD)) {
        const lead = lastU >= lastD ? lastU : lastD
        const leadCol = lastU >= lastD ? (cfgNow.upCol || SD_DEFAULTS.upCol) : (cfgNow.downCol || SD_DEFAULTS.downCol)
        const cyRaw = yOf(lead)
        const cy = Math.max(8, Math.min(H - 8, cyRaw))
        if (cyRaw >= 0 && cyRaw <= H) {
          ctx.save()
          ctx.strokeStyle = leadCol
          ctx.globalAlpha = 0.55
          ctx.lineWidth = 1
          ctx.setLineDash([4, 4])
          ctx.beginPath()
          ctx.moveTo(0, Math.round(cyRaw) + 0.5)
          ctx.lineTo(plotW, Math.round(cyRaw) + 0.5)
          ctx.stroke()
          ctx.restore()
        }
        ctx.fillStyle = leadCol
        ctx.fillRect(plotW + 1, cy - 8, W - plotW - 2, 16)
        ctx.fillStyle = '#0b0d12'
        setCtxFont(ctx, '600 14px "Nova Square", system-ui, sans-serif')
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(fmtV(lead), plotW + 1 + (W - plotW - 2) / 2, cy)
        ctx.textBaseline = 'alphabetic'
      }
    }
    rafRef.current = requestFrame(draw, FRAME_LAYER)
    if (paintNowRef) paintNowRef.current = () => { if (!live) return; cancelFrame(rafRef.current); draw() }
    return () => {
      live = false; cancelFrame(rafRef.current)
      if (paintNowRef) paintNowRef.current = null
    }
  }, [candles, height, barMinutes])

  const hideHorizontal = () => {
    if (hlRef.current) hlRef.current.style.display = 'none'
    if (chipRef.current) chipRef.current.style.display = 'none'
  }
  const onPointerMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    const plotW = Math.max(50, (containerWidthRef.current || rect.width) - (axisWidth || FIXED_OVERLAY_WIDTH))
    selfCursorXRef.current = x <= plotW ? x : -1
    if (x > plotW) {
      if (mouseXRef.current !== -1) { mouseXRef.current = -1; if (onCursorXChange) onCursorXChange(-1); else placeCrosshairs(mouseXRef, -1) }
      hideHorizontal()
      return
    }
    const mainX = x
    mouseXRef.current = mainX
    if (onCursorXChange) onCursorXChange(mainX)
    else placeCrosshairs(mouseXRef, mainX)
    const y = e.clientY - rect.top
    const hl = hlRef.current
    if (hl) {
      const RH = rowHeightRef?.current || ROW_HEIGHT
      hl.style.top = `${y}px`
      hl.style.left = '0px'
      hl.style.width = `${plotW}px`
      hl.style.height = `${RH}px`
      hl.style.display = 'block'
    }
    const chip = chipRef.current
    if (chip) {
      const { yMin, yMax, topPad, botPad, H } = yScaleRef.current
      const span = Math.max(1, (H || height) - (topPad || 6) - (botPad || 6))
      const range = (yMax - yMin) || 1
      const raw = yMax - ((y - (topPad || 6)) / span) * range
      const v = Math.max(yMin, Math.min(yMax, raw))
      let textEl = chip.firstElementChild
      if (!textEl || textEl.className !== 'price-label-text') {
        chip.textContent = ''
        textEl = document.createElement('span')
        textEl.className = 'price-label-text'
        chip.appendChild(textEl)
      }
      textEl.textContent = Math.abs(v) < range * 0.02 ? '0' : (v < 0 ? '-' : '') + fmtV(v)
      chip.style.top = `${y}px`
      chip.style.left = `${plotW}px`
      chip.style.width = `${rect.width - plotW}px`
      chip.style.display = 'flex'
    }
  }
  const onPointerLeave = () => {
    selfCursorXRef.current = -1
    mouseXRef.current = -1
    if (onCursorXChange) onCursorXChange(-1)
    else placeCrosshairs(mouseXRef, -1)
    hideHorizontal()
  }

  const cfg = { ...SD_DEFAULTS, ...config }
  return (
    <div ref={wrapRef} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}
      style={{ position: 'relative', height, flexShrink: 0, borderTop: '1px solid #1a1d24', background: '#0b0d12', touchAction: 'none', cursor: 'crosshair' }}>
      <canvas ref={plotRef} style={{ display: 'block' }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: axisWidth || FIXED_OVERLAY_WIDTH, overflow: 'hidden', pointerEvents: 'none' }}>
        <div ref={xhairRef} className="chart-crosshair" style={CROSSHAIR_PANE_STYLE} />
      </div>
      <div ref={hlRef} className="chart-h-highlight" style={{ display: 'none', transform: 'translateY(-50%)' }} />
      <div ref={chipRef} className="price-label" style={{ display: 'none', transform: 'translateY(-50%)' }} />
      <div style={{ position: 'absolute', top: 2, left: 6, font: '600 9px "Nova Square", system-ui, sans-serif', color: '#5a668c', letterSpacing: '1px', pointerEvents: 'none' }}>
        S/D {cfg.mode === 'run' ? '· RUN' : ''} · EMA{cfg.smoothLen}
        <span style={{ color: cfg.upCol, marginLeft: 6 }}>UP</span>
        <span style={{ color: cfg.downCol, marginLeft: 4 }}>DN</span>
      </div>

      <div
        style={{
          position: 'absolute', left: '50%', bottom: 3, transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 2,
          padding: '2px 4px', borderRadius: 4,
          background: 'rgba(12,14,18,0.72)', border: '1px solid #23262d',
          opacity: scaleUiHot ? 1 : 0.28, transition: 'opacity 0.15s', zIndex: 3,
        }}
        onMouseEnter={() => setScaleUiHot(true)}
        onMouseLeave={() => setScaleUiHot(false)}
        onPointerDown={(e) => { e.stopPropagation(); setScaleUiHot(true) }}
        onPointerMove={(e) => e.stopPropagation()}
      >
        {SD_SCALES.map(v => (
          <button
            key={v}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setXScale(v) }}
            title={v === 1 ? 'Match the chart' : `${v}× more history in the same width`}
            style={{
              background: v === xScale ? '#2f6fd622' : 'transparent',
              border: '1px solid ' + (v === xScale ? '#2f6fd666' : 'transparent'),
              color: v === xScale ? '#2f6fd6' : '#6b7280',
              font: '600 10px "Nova Square", system-ui, sans-serif',
              padding: '1px 5px', borderRadius: 3, cursor: 'pointer', lineHeight: 1.4,
            }}
          >
            {v}×
          </button>
        ))}
      </div>
    </div>
  )
}
